export class ApiError extends Error {
    status: number
    code: string | null

    constructor(message: string, status: number, code: string | null = null) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.code = code
    }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** Calls one of this site's API routes and throws an ApiError with a readable message on failure. */
export const apiFetch = async <T = unknown>(url: string, options: { method?: Method, body?: unknown } = {}): Promise<T> => {
    let response: Response

    try {
        response = await fetch(url, {
            method: options.method ?? (options.body === undefined ? 'GET' : 'POST'),
            headers: options.body === undefined ? undefined : { 'Content-Type': 'application/json' },
            body: options.body === undefined ? undefined : JSON.stringify(options.body),
            credentials: 'same-origin'
        })
    }
    catch {
        throw new ApiError('You appear to be offline. Check your connection and try again.', 0, 'offline')
    }

    if (response.status === 204) return undefined as T

    const data = await response.json().catch(() => null) as { message?: string, code?: string } | null

    if (!response.ok) {
        throw new ApiError(data?.message ?? 'Something went wrong. Please try again.', response.status, data?.code ?? null)
    }

    return data as T
}

export const errorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : 'Something went wrong. Please try again.'
